import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO } from "./Brand";

/**
 * DEBSUN Boot Sequence — the official, reusable brand animation.
 * Six stages: Initialization → Intelligence Ignites → Infrastructure →
 * Structure → Energy → Identity. Duration-adaptable.
 *
 * Props:
 *  - duration   ms for the full run (website=3000, intro=6000)
 *  - context    'website' (tagline line) | 'full' (name + tagline) | 'compact'
 *  - onDone      called after the exit fade
 *  - showSkip    render a subtle Skip control (default true)
 *  - hold        if true, holds on Identity instead of auto-exiting (for /intro)
 */
export default function BootSequence({
  duration = 3000,
  context = "website",
  onDone,
  showSkip = true,
  hold = false,
  hideChrome = false,
}) {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("run"); // run | exit
  const [muted, setMuted] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("debsun-boot-muted") === "1"
  );
  const rafRef = useRef();
  const audioRef = useRef(null);
  const startRef = useRef(0);

  const finish = useCallback(() => {
    setPhase("exit");
    setTimeout(() => onDone?.(), 650);
  }, [onDone]);

  const skip = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setProgress(1);
    finish();
  }, [finish]);

  // progress driver
  useEffect(() => {
    startRef.current = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - startRef.current) / duration);
      setProgress(p);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else if (!hold) finish();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration, hold, finish]);

  // ambient industrial hum (best-effort; browsers may block autoplay w/o gesture)
  useEffect(() => {
    if (muted) return;
    let ctx, osc, sub, gain;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
      audioRef.current = ctx;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass"; filter.frequency.value = 240;
      gain = ctx.createGain(); gain.gain.value = 0;
      osc = ctx.createOscillator(); osc.type = "sine"; osc.frequency.value = 56;
      sub = ctx.createOscillator(); sub.type = "sine"; sub.frequency.value = 84;
      osc.connect(filter); sub.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
      const t = ctx.currentTime;
      const secs = duration / 1000;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.05, t + 0.9);
      gain.gain.setValueAtTime(0.05, t + Math.max(0.9, secs - 0.7));
      gain.gain.linearRampToValueAtTime(0, t + secs);
      osc.start(); sub.start();
      ctx.resume?.();
    } catch { /* audio unavailable — silent */ }
    return () => { try { osc?.stop(); sub?.stop(); ctx?.close(); } catch { /* noop */ } };
  }, [muted, duration]);

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem("debsun-boot-muted", next ? "1" : "0");
      if (next) { try { audioRef.current?.close(); } catch { /* noop */ } }
      return next;
    });
  };

  // ---- stage windows (fractions of duration) ----
  const S = { init: 0.12, intel: 0.34, infra: 0.52, structure: 0.72, energy: 0.86 };
  const stage =
    progress < S.init ? 1 :
    progress < S.intel ? 2 :
    progress < S.infra ? 3 :
    progress < S.structure ? 4 :
    progress < S.energy ? 5 : 6;

  const STAGE_LABELS = [
    "INITIALIZING", "INTELLIGENCE", "INFRASTRUCTURE", "STRUCTURE", "ENERGY", "IDENTITY",
  ];

  const showAurora = progress >= S.init;
  const showCad = progress >= S.intel;
  const showSteel = progress >= S.infra;
  const showCorona = progress >= S.structure;
  const showLogo = progress >= S.energy;

  const draw = (d = 0) => ({
    initial: { pathLength: 0, opacity: 0 },
    animate: { pathLength: 1, opacity: 1 },
    transition: { duration: 0.5, delay: d, ease: [0.19, 1, 0.22, 1] },
  });

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden select-none"
      style={{ background: "var(--navy)", pointerEvents: phase === "exit" ? "none" : "auto" }}
      initial={{ opacity: 1 }} animate={{ opacity: phase === "exit" ? 0 : 1 }}
      transition={{ duration: 0.65, ease: "easeInOut" }}
      data-testid="boot-sequence"
      aria-hidden={phase === "exit" ? "true" : undefined}
    >
      {/* deep-space vignette */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(62% 52% at 50% 46%, rgba(16,21,31,.5), #0B0F14 82%)" }} />

      {/* Stage 03 — Infrastructure: CAD grid */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: showCad ? (stage >= 5 ? 0.10 : 0.22) : 0 }}
        transition={{ duration: 0.8 }}
        style={{
          backgroundImage:
            "linear-gradient(rgba(107,114,128,.28) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(107,114,128,.28) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(circle at 50% 48%, black 30%, transparent 72%)",
          WebkitMaskImage: "radial-gradient(circle at 50% 48%, black 30%, transparent 72%)",
        }}
      />

      {/* Stage 02 — Intelligence Ignites: aurora + signal pulses (fiber network) */}
      <motion.div
        className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[320px] w-full"
        initial={{ opacity: 0, scaleX: 0.3 }}
        animate={{ opacity: showAurora ? 1 : 0, scaleX: showAurora ? Math.min(1, 0.5 + progress) : 0.3 }}
        transition={{ duration: 0.7 }}
      >
        <FiberAurora active={showAurora} />
      </motion.div>

      {/* central construction */}
      <div className="relative w-[300px] h-[300px] md:w-[380px] md:h-[380px] flex items-center justify-center">
        {/* Stage 01 — Initialization pulse */}
        <AnimatePresence>
          {stage === 1 && (
            <motion.span
              className="absolute w-3 h-3 rounded-full"
              style={{ background: "var(--teal)", boxShadow: "0 0 24px 6px rgba(0,229,212,.55)" }}
              initial={{ scale: 0.4, opacity: 0.2 }}
              animate={{ scale: [0.6, 1.3, 0.6], opacity: [0.4, 1, 0.4] }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {!showLogo && (
            <motion.svg viewBox="0 0 400 400" fill="none" className="absolute inset-0 w-full h-full"
              initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              {/* Stage 03 — engineering / railway geometry (CAD-drawn) */}
              {showCad && (
                <>
                  <motion.line x1={70} y1={330} x2={330} y2={330} stroke="#6B7280" strokeWidth="1" {...draw(0)} />
                  <motion.path d="M150 330 L188 250" stroke="#98A0AC" strokeWidth="2" strokeLinecap="round" {...draw(0.05)} />
                  <motion.path d="M250 330 L212 250" stroke="#98A0AC" strokeWidth="2" strokeLinecap="round" {...draw(0.1)} />
                  {[0, 1, 2, 3].map((r) => (
                    <motion.line key={r} x1={158 + r * 8} y1={262 + r * 17} x2={242 - r * 8} y2={262 + r * 17}
                      stroke="#6B7280" strokeWidth="1.6" {...draw(0.14 + r * 0.05)} />
                  ))}
                  {/* dimension ticks */}
                  <motion.line x1={70} y1={324} x2={70} y2={336} stroke="#6B7280" strokeWidth="1" {...draw(0.2)} />
                  <motion.line x1={330} y1={324} x2={330} y2={336} stroke="#6B7280" strokeWidth="1" {...draw(0.22)} />
                </>
              )}

              {/* Stage 04 — Structure: steel pillars rise, panels lock */}
              {showSteel && [122, 145, 168].map((x, i) => (
                <motion.rect key={x} x={x} y={120} width={8} height={130} rx={2.5}
                  fill={i === 2 ? "#6B7280" : "#98A0AC"}
                  initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }}
                  style={{ transformOrigin: `${x + 4}px 250px` }}
                  transition={{ duration: 0.5, delay: i * 0.12, ease: [0.19, 1, 0.22, 1] }} />
              ))}

              {/* Stage 05 — Energy: golden corona ignites (dashed arc + solid D curve) */}
              {showCorona && (
                <>
                  <motion.circle cx={252} cy={188} r={78} stroke="#D4AF37" strokeWidth="7" strokeLinecap="round" strokeDasharray="4 12"
                    {...draw(0)} style={{ transformOrigin: "252px 188px" }} />
                  <motion.path d="M252 110 A 78 78 0 1 1 186 226" stroke="#D4AF37" strokeWidth="9" strokeLinecap="round" fill="none" {...draw(0.12)} />
                  {/* one confident glow pulse */}
                  <motion.circle cx={252} cy={188} r={78} stroke="#D4AF37" strokeWidth="2" fill="none"
                    initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: [0, 0.6, 0], scale: [0.9, 1.15, 1.25] }}
                    transition={{ duration: 1, delay: 0.25, ease: "easeOut" }} style={{ transformOrigin: "252px 188px" }} />
                </>
              )}
            </motion.svg>
          )}
        </AnimatePresence>

        {/* Stage 06 — Identity: the real logo resolves */}
        <motion.img
          src={LOGO} alt="DEBSUN Technologies"
          className="absolute inset-0 w-full h-full object-contain"
          initial={{ opacity: 0, scale: 0.92, filter: "blur(8px)" }}
          animate={{ opacity: showLogo ? 1 : 0, scale: showLogo ? 1 : 0.92, filter: showLogo ? "blur(0px)" : "blur(8px)" }}
          transition={{ duration: 0.85, ease: [0.19, 1, 0.22, 1] }}
        />
      </div>

      {/* Stage 01 label + Stage 06 identity text */}
      <div className="relative mt-4 h-14 flex flex-col items-center justify-center text-center px-6">
        <AnimatePresence mode="wait">
          {stage === 1 && (
            <motion.div key="init" className="font-mono-2 text-[11px] tracking-[0.4em] uppercase"
              style={{ color: "rgba(0,229,212,.85)" }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              System Initializing<span className="animate-pulse">_</span>
            </motion.div>
          )}
          {showLogo && (
            <motion.div key="identity" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              {context === "full" && (
                <div className="display text-lg md:text-xl mb-1">
                  <span style={{ color: "#98A0AC" }}>DEBSUN</span> <span className="text-muted-foreground font-normal">Technologies</span>
                </div>
              )}
              <div className="font-serif-2 italic text-sm md:text-base" style={{ color: "rgba(251,250,247,.8)" }}>
                {context === "compact" ? "Intelligence in Motion." : "Engineering Practical Technologies for a Smarter Tomorrow."}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* progress rail + stage labels */}
      <div className="absolute bottom-9 left-0 right-0 flex flex-col items-center gap-3 px-6">
        <div className="w-[220px] h-px" style={{ background: "rgba(255,255,255,.12)" }}>
          <div className="h-full" style={{ width: `${progress * 100}%`, background: "linear-gradient(90deg,var(--aurora-green),var(--teal),var(--purple),var(--gold))", transition: "width .08s linear" }} />
        </div>
        <div className="flex gap-3 md:gap-4 flex-wrap justify-center">
          {STAGE_LABELS.map((s, i) => (
            <span key={s} className="font-mono-2 text-[8px] tracking-[0.24em] transition-colors duration-300"
              style={{ color: i < stage ? "#D4AF37" : "rgba(215,219,226,.22)" }}>
              {String(i + 1).padStart(2, "0")} {s}
            </span>
          ))}
        </div>
      </div>

      {/* controls */}
      {!hideChrome && (
        <button onClick={toggleMute} data-testid="boot-mute-btn" aria-label="Toggle sound"
          className="absolute bottom-9 left-8 font-mono-2 text-[10px] tracking-[0.2em] uppercase text-[color:var(--console-text)]/55 hover:text-[color:var(--console-text)]/90 transition-colors">
          {muted ? "Sound Off" : "Sound On"}
        </button>
      )}
      {showSkip && !hold && !hideChrome && (
        <button onClick={skip} data-testid="boot-skip-btn"
          className="absolute bottom-9 right-8 font-mono-2 text-[10px] tracking-[0.2em] uppercase text-[color:var(--console-text)]/55 hover:text-[color:var(--gold)] transition-colors">
          Skip →
        </button>
      )}
    </motion.div>
  );
}

/** Aurora ribbon that reads as data propagating through a fiber network. */
function FiberAurora({ active }) {
  const path = "M-100 160 C 200 90, 380 240, 640 160 S 1100 70, 1340 165 S 1720 250, 2000 150";
  return (
    <svg viewBox="0 0 1440 320" preserveAspectRatio="none" className="w-full h-full" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="fib" x1="0" y1="0" x2="1440" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#34E3B0" stopOpacity="0" />
          <stop offset="20%" stopColor="#34E3B0" />
          <stop offset="45%" stopColor="#00E5D4" />
          <stop offset="68%" stopColor="#8A5CF6" />
          <stop offset="88%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </linearGradient>
        <filter id="fibglow" x="-10%" y="-80%" width="120%" height="260%">
          <feGaussianBlur stdDeviation="5" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g filter="url(#fibglow)">
        <path d={path} stroke="url(#fib)" strokeWidth="2.5" strokeLinecap="round" className="aurora-flow" />
        <path d="M-100 190 C 260 130, 440 270, 700 195 S 1140 120, 1400 205 S 1760 270, 2040 190"
          stroke="url(#fib)" strokeWidth="1.5" strokeOpacity="0.6" className="aurora-flow-2" />
      </g>
      {/* signal pulses travelling along the fiber */}
      {active && [0, 1, 2, 3, 4].map((i) => (
        <circle key={i} r={i % 2 ? 2 : 3} fill={i % 3 === 0 ? "#34E3B0" : i % 3 === 1 ? "#00E5D4" : "#D4AF37"}>
          <animateMotion dur={`${2.4 + i * 0.5}s`} repeatCount="indefinite" begin={`${i * 0.4}s`} path={path} />
          <animate attributeName="opacity" values="0;1;1;0" dur={`${2.4 + i * 0.5}s`} repeatCount="indefinite" begin={`${i * 0.4}s`} />
        </circle>
      ))}
    </svg>
  );
}
